import { isEdgeFunctionUrl } from './supabase/client';

const EDGE_UNAVAILABLE_COOLDOWN_MS = 30_000;

let unavailableUntil = 0;
let lastWarnAt = 0;

function now(): number {
  return Date.now();
}

export function isLikelyEdgeTransportError(error: unknown): boolean {
  if (!error) return false;
  if ((error as { name?: string }).name === 'AbortError') return false;
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('Failed to fetch') ||
    message.includes('NetworkError') ||
    message.includes('Load failed') ||
    message.includes('network error')
  );
}

export function isEdgeBackendUnavailable(): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }
  return unavailableUntil > now();
}

export function markEdgeBackendAvailable(): void {
  unavailableUntil = 0;
}

export function markEdgeBackendUnavailable(error?: unknown): void {
  if (!isLikelyEdgeTransportError(error) && typeof navigator !== 'undefined' && navigator.onLine !== false) {
    return;
  }
  unavailableUntil = Math.max(unavailableUntil, now() + EDGE_UNAVAILABLE_COOLDOWN_MS);
}

export function shouldAttemptEdgeRequest(url?: string): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }
  if (url && !isEdgeFunctionUrl(url)) {
    return true;
  }
  return !isEdgeBackendUnavailable();
}

export function logEdgeTransportIssueOnce(scope: string, error: unknown): void {
  // Only log genuine transport/network errors, not JS runtime errors
  if (!isLikelyEdgeTransportError(error)) return;
  const current = now();
  if (current - lastWarnAt < 10_000) return;
  lastWarnAt = current;
  let message: string;
  if (error instanceof Error) {
    message = error.message || error.toString();
  } else if (error !== null && typeof error === 'object') {
    // JSON.stringify on a WebKit Error yields {line, column, sourceURL} not the message.
    // Prefer .message / .toString() when available.
    const asObj = error as Record<string, unknown>;
    if (typeof asObj['message'] === 'string') {
      message = asObj['message'] as string;
    } else {
      try {
        message = JSON.stringify(error);
      } catch {
        message = Object.prototype.toString.call(error);
      }
    }
  } else {
    message = String(error);
  }
  console.warn(`[${scope}] Edge backend unavailable: ${message}`);
}
